
























































import { Vue, Component, InjectReactive } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import { AgGridVue } from '@ag-grid-community/vue';
import { GridReadyEvent, ValueFormatterParams } from '@ag-grid-community/core';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import ContentDialog from '@/components/content-dialog.vue';
import ClientModel from '@/models/crm/client.model';
import AgGridWrapper from '@/components/ag-grid-wrapper.vue';
import { IGridConfig } from '@/interfaces/grid-config.interface';
import ActivityService from '@/services/crm/activity.service';
import dayjs from '@/plugins/dayjs';
import ActivityEventModel from '@/models/crm/activity-event.model';
import { GridHelper } from '@/utils/helpers/grid-helper';
import { DateHelper } from '@/utils/helpers/date-helper';
import RouterService from '@/services/router.service';
import UserContactInfo from '@/models/crm/user-contact-info.model';
import { IKeyValue } from '@/interfaces/key-value.interface';
import { IDialogConfig } from '@/interfaces/dialog-config.interface';
import CrmActivityCalendarEventForm from '../../components/crm/activity-calendar-event-form.vue';
import ProcessModel from '@/models/crm/process.model';
import { IGridCellEvent } from '@/interfaces/grid-cell-clicked.interface';
import { ClientTypeEnum } from '@/enums/client-type.enum';

@Component({
  components: {
    AgGridWrapper,
    AgGridVue,
    ContentDialog,
    CrmActivityCalendarEventForm,
  },
})
export default class CrmActivities extends Vue {
  @inject(InjectionIdEnum.CrmActivityService)
  private activityService!: ActivityService;

  @inject(InjectionIdEnum.RouterService)
  private routerService!: RouterService;

  @InjectReactive('activeClient') readonly activeClient!: ClientModel;

  @InjectReactive('clientType') readonly clientType!: ClientTypeEnum;

  @InjectReactive('userContactInfo') readonly userContactInfo!: UserContactInfo;

  processId: number | null = null;

  dialogConfig: IKeyValue<IDialogConfig> = {
    eventForm: {
      id: null,
      show: false,
      startDate: dayjs().toDate(),
      endDate: dayjs().toDate(),
      process: null,
    },
  };

  grid: GridReadyEvent | null = null;

  gridSettings: IGridConfig = {
    loading: false,
    defaultSort: [{ colId: 'dataHoraInicio', sort: 'desc' }],
    columnDefs: [
      {
        headerName: `${this.$t('crm.view.activities.grid.schedule')}`,
        field: 'titulo',
        minWidth: 130,
      },
      {
        headerName: `${this.$t('crm.view.activities.grid.dateAndHour')}`,
        field: 'dataHoraInicio',
        maxWidth: 155,
        valueGetter: (params): string => GridHelper.valueGetter(params, DateHelper.formatToIsoDateTimeString),
        valueFormatter: (params: ValueFormatterParams): string => {
          const format = params.data.timed ? 'dateTime' : 'short';
          return `${this.$d(params.data.dataHoraInicio, format)}`;
        },
        cellClass: 'dateISO',
      },
      {
        headerName: `${this.$t('crm.view.activities.grid.attendant')}`,
        field: 'atendente.nome',
        minWidth: 130,
        flex: 0.5,
      },
      {
        headerName: `${this.$t('crm.view.activities.grid.historyType')}`,
        field: 'tipoHistorico.nome',
        minWidth: 130,
      },
    ],
  };

  items: ActivityEventModel[] = [];

  private readonly startAt = '2010-01-01';

  private readonly endAt = dayjs().add(365, 'day').format('YYYY-MM-DD');

  async mounted(): Promise<void> {
    this.processId = this.getProcessIdFromRoute();

    if (!this.processId) {
      this.$notify.error(this.$t('crm.view.activities.message.emptyProcessId'));
      return;
    }

    const process = new ProcessModel();
    process.id = this.processId;
    this.dialogConfig.eventForm.process = process;

    this.loadItems();
  }

  async onCellClick(event: IGridCellEvent<ActivityEventModel>): Promise<void> {
    if (!event.data) {
      return;
    }

    this.dialogConfig.eventForm.id = event.data.id;
    this.dialogConfig.eventForm.show = true;
  }

  onAddEvent(): void {
    this.dialogConfig.eventForm.id = null;
    this.dialogConfig.eventForm.show = true;
  }

  async onAfterSaveEvent(): Promise<void> {
    this.loadItems();
  }

  get eventFormDialogTitle(): string {
    let titleKey = 'crm.activityCalendar.dialog.calendarEventForm.newTitle';

    if (this.dialogConfig.eventForm.id) {
      titleKey = 'crm.activityCalendar.dialog.calendarEventForm.editTitle';
    }

    return `${this.$t(titleKey)}`;
  }

  private async loadItems(): Promise<void> {
    this.gridSettings.loading = true;
    this.items = await this.activityService.getEventsByProcess(this.processId as number);
    this.gridSettings.loading = false;
  }

  private getClientIdFromRoute(): string {
    if (this.clientType === ClientTypeEnum.Prospect) {
      const currentRoute = this.routerService.route();
      return currentRoute.params && currentRoute.params.clientId;
    }
    return this.activeClient.cnpjCpf;
  }

  private getProcessIdFromRoute(): number {
    const currentRoute = this.routerService.route();
    return (currentRoute.query && currentRoute.query.processId) as unknown as number;
  }
}
